const principalTypes = [
  "device",
  "integration",
  "invocation",
  "user",
  "org",
] as const;

export type PrincipalType = (typeof principalTypes)[number];

function isValidPrincipalType(
  maybePrincipalType: string,
): maybePrincipalType is PrincipalType {
  return (principalTypes as readonly string[]).includes(maybePrincipalType);
}

export class Principal {
  constructor(
    readonly ptype: PrincipalType,
    readonly id: string,
  ) {
    if (id.includes(":")) {
      throw new Error(`ID cannot contain a colon, got ${id}`);
    }
  }

  static forDevice(deviceId: string, orgId: string): Principal {
    return new Principal("device", `${deviceId}@${orgId}`);
  }

  static forIntegration(integrationId: string): Principal {
    return new Principal("integration", integrationId);
  }

  static forInvocation(invocationId: string): Principal {
    return new Principal("invocation", invocationId);
  }

  static forOrg(orgId: string): Principal {
    return new Principal("org", orgId);
  }

  static forUser(userId: string): Principal {
    return new Principal("user", userId);
  }

  static fromString(
    serialized: string,
  ):
    | { ok: Principal; error?: undefined }
    | { ok?: undefined; error: PrincipalParseError } {
    const partsFound = serialized.split(":");
    if (partsFound.length !== 2) {
      return {
        error: new PrincipalParseError(
          "Principal must be serialized in the form `type:id`",
          { serialized, partsFound },
        ),
      };
    }
    const [principalType, id] = partsFound;
    if (!isValidPrincipalType(principalType)) {
      return {
        error: new PrincipalParseError("Principal type was invalid", {
          serialized,
          principalType,
        }),
      };
    }
    return { ok: new Principal(principalType, id) };
  }

  toString(): string {
    return `${this.ptype}:${this.id}`;
  }

  get unqualifiedId(): string {
    if (this.ptype === "user") {
      return this.id;
    }
    return this.id.split("@")[0];
  }
}

export class PrincipalParseError extends Error {
  constructor(
    message: string,
    readonly options: ErrorOptions & {
      serialized: string;
      partsFound?: string[];
      principalType?: string;
    },
  ) {
    super(message);
  }
}
